import { useMemo, useState } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { LeadsControleGrupo, LeadsControlePaginas } from '@/types/leadsDashboard';
import { diaCurto, n, pct } from '../format';

/**
 * Bloco 5.7 — Controle de páginas.
 *
 * Responde três perguntas, nesta ordem: QUANTAS trabalham, se isso está
 * SUBINDO OU CAINDO, e QUAIS são.
 *
 * A terceira vinha antes despejando os quatro grupos abertos ao mesmo tempo —
 * oitocentos nomes de página empilhados, que é onde a informação morria. Agora
 * um grupo por vez, sob demanda e com busca. O clique para filtrar a tela
 * continua, que era a parte boa.
 *
 * A classificação é calculada a partir do PERÍODO selecionado, não lida de um
 * campo de status: uma página em "Só captação" em 30 dias é problema real; em
 * "Hoje" pode ser só um dia sem disparo.
 */

const DESCRICOES: Record<string, string> = {
    produtiva: 'captam, engajam e geram receita',
    captacao: 'trazem lead, mas receita zero',
    dormente: 'têm base, nada aconteceu',
    pausada: 'desativadas, fora de toda conta',
};

const CORES: Record<string, string> = {
    produtiva: 'var(--green)',
    captacao: 'var(--blue)',
    dormente: 'var(--amber)',
    pausada: 'var(--ink3)',
};

interface Props {
    dados: LeadsControlePaginas;
    rotulo: string;
    paginaSelecionada: string | null;
    onPagina: (id: string | null) => void;
}

export default function ControlePaginas({
    dados, rotulo, paginaSelecionada, onPagina,
}: Props) {
    const [aberto, setAberto] = useState<string | null>(null);
    const [busca, setBusca] = useState('');

    const total = dados.cadastradas || 1;
    const serie = dados.serie ?? [];
    // Um ponto não é uma tendência. Com "Hoje" no filtro o gráfico sairia com
    // uma coluna só, que ocupa espaço e não diz nada.
    const temSerie = serie.length > 1;

    const grupoAberto = useMemo(
        () => dados.grupos.find(g => g.chave === aberto) ?? null,
        [dados.grupos, aberto],
    );

    const itensFiltrados = useMemo(() => {
        if (!grupoAberto) return [];
        const q = busca.trim().toLowerCase();
        if (!q) return grupoAberto.itens;
        return grupoAberto.itens.filter(
            p => p.nome.toLowerCase().includes(q) || p.pais.toLowerCase() === q,
        );
    }, [grupoAberto, busca]);

    const abrir = (chave: string) => {
        setBusca('');
        setAberto(a => (a === chave ? null : chave));
    };

    const contadores = [
        { l: 'Captaram lead', v: dados.com_lead, cor: 'var(--blue)' },
        { l: 'Geraram clique', v: dados.com_clique, cor: 'var(--teal)' },
        { l: 'Geraram receita', v: dados.com_receita, cor: 'var(--green)' },
        { l: 'Sem atividade', v: dados.sem_atividade, cor: 'var(--amber)' },
    ];

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Controle de páginas</div>
                    <div className="card-s">
                        Quantas das {n(dados.cadastradas)} páginas ativas realmente trabalharam
                        {' '}· {rotulo}
                        {!!dados.pausadas && (
                            <> · {n(dados.pausadas)} pausadas ficam fora de toda conta</>
                        )}
                    </div>
                </div>
                <div className="card-s">
                    <b className="num" style={{ color: 'var(--ink)', fontSize: 13 }}>
                        {dados.paginas_para_80pct_receita}
                    </b>{' '}
                    de {dados.cadastradas} concentram 80% da receita
                </div>
            </div>

            {/* Os quatro contadores viraram medidores: o número sozinho não diz
                se 171 de 296 é bom, a barra diz. */}
            <div className="cpmedidores">
                {contadores.map(c => (
                    <div key={c.l} className="cpmed">
                        <div className="cpmed-t">{c.l}</div>
                        <div className="cpmed-v">
                            <b className="num" style={{ color: c.cor }}>{n(c.v)}</b>
                            <span>{pct((c.v / total) * 100, 0)}</span>
                        </div>
                        <div className="cpmed-bar">
                            <i style={{ width: `${Math.min((c.v / total) * 100, 100)}%`, background: c.cor }} />
                        </div>
                    </div>
                ))}
            </div>

            {temSerie && (
                <div className="cpchart">
                    <div className="cpchart-t">Páginas que trabalharam por dia</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <AreaChart data={serie} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                            <defs>
                                <linearGradient id="cpLead" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--blue)" stopOpacity={0.28} />
                                    <stop offset="100%" stopColor="var(--blue)" stopOpacity={0.02} />
                                </linearGradient>
                                <linearGradient id="cpClique" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--teal)" stopOpacity={0.24} />
                                    <stop offset="100%" stopColor="var(--teal)" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="2 4" stroke="var(--line2)" vertical={false} />
                            <XAxis
                                dataKey="data" tickFormatter={diaCurto}
                                tick={{ fontSize: 10, fill: 'var(--ink3)' }}
                                axisLine={false} tickLine={false} minTickGap={18}
                            />
                            <YAxis
                                tick={{ fontSize: 10, fill: 'var(--ink3)' }}
                                axisLine={false} tickLine={false} width={44}
                            />
                            <Tooltip
                                labelFormatter={(v) => diaCurto(String(v))}
                                formatter={(v, k) => [
                                    `${n(Number(v ?? 0))} páginas`,
                                    k === 'com_lead' ? 'Captaram lead' : 'Geraram clique',
                                ]}
                                contentStyle={{
                                    fontSize: 12, borderRadius: 6,
                                    border: '1px solid var(--line)',
                                }}
                            />
                            <Area
                                type="monotone" dataKey="com_lead" stroke="var(--blue)"
                                strokeWidth={2} fill="url(#cpLead)"
                            />
                            <Area
                                type="monotone" dataKey="com_clique" stroke="var(--teal)"
                                strokeWidth={2} fill="url(#cpClique)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                    {/* Receita não entra aqui: ela é medida por campanha no
                        período inteiro, não existe "páginas com receita no
                        dia 12". Inventar a série faria o gráfico mentir. */}
                    <div className="cpchart-l">
                        <span><i style={{ background: 'var(--blue)' }} /> Captaram lead</span>
                        <span><i style={{ background: 'var(--teal)' }} /> Geraram clique</span>
                    </div>
                </div>
            )}

            <div className="cpgrupos">
                {dados.grupos.map(g => (
                    <CartaoGrupo
                        key={g.chave}
                        grupo={g}
                        total={total}
                        aberto={aberto === g.chave}
                        onAbrir={() => abrir(g.chave)}
                    />
                ))}
            </div>

            {grupoAberto && (
                <div className="cplista">
                    <div className="cplista-h">
                        <span>
                            <b style={{ color: CORES[grupoAberto.chave] }}>{grupoAberto.rotulo}</b>
                            {' · '}{n(itensFiltrados.length)}
                            {busca && ` de ${n(grupoAberto.itens.length)}`}
                        </span>
                        <input
                            type="search" value={busca} autoFocus
                            onChange={e => setBusca(e.target.value)}
                            placeholder="Filtrar por nome ou país…"
                            aria-label="Filtrar páginas do grupo"
                        />
                        <button type="button" className="clearall" onClick={() => setAberto(null)}>
                            fechar
                        </button>
                    </div>
                    {itensFiltrados.length === 0 ? (
                        <div className="empty">Nenhuma página com esse termo.</div>
                    ) : (
                        <div className="cpchips">
                            {itensFiltrados.map(p => (
                                <button
                                    key={p.id} type="button" className="pq-chip"
                                    data-on={paginaSelecionada === p.id ? 1 : 0}
                                    onClick={() => onPagina(paginaSelecionada === p.id ? null : p.id)}
                                    title="Filtrar a tela por esta página"
                                >
                                    <span className="code">{p.pais}</span>{p.nome}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CartaoGrupo({ grupo, total, aberto, onAbrir }: {
    grupo: LeadsControleGrupo;
    total: number;
    aberto: boolean;
    onAbrir: () => void;
}) {
    const cor = CORES[grupo.chave];
    // `participacao_pct` vem null em `pausada`, de propósito: ela não é fatia
    // das ativas. Com o denominador das ativas a tela mostrava 170%.
    const share = grupo.participacao_pct;
    return (
        <button
            type="button"
            className="cpgrupo"
            data-on={aberto ? 1 : 0}
            style={{ ['--c' as string]: cor }}
            onClick={onAbrir}
        >
            <div className="cpgrupo-t">{grupo.rotulo}</div>
            <div className="cpgrupo-v">
                <b className="num" style={{ color: cor }}>{n(grupo.quantidade)}</b>
                <span>{share === null ? 'fora da conta' : `${pct(share, 0)} das ativas`}</span>
            </div>
            <div className="cpgrupo-bar">
                <i style={{
                    width: `${Math.min((grupo.quantidade / total) * 100, 100)}%`,
                    background: cor,
                }} />
            </div>
            <div className="cpgrupo-d">{DESCRICOES[grupo.chave]}</div>
            <div className="cpgrupo-a">
                {aberto ? 'ocultar' : `ver as ${n(grupo.quantidade)}`}
            </div>
        </button>
    );
}
